import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { FloatingParticles } from './FloatingParticles';
import { Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const handleJoin = () => {
    toast.success('Welcome to Radiance Rewards!', {
      description: 'You have been enrolled. Your 500 GP welcome bonus has been added.',
    });
  };

  return (
    <section id="join" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1516815989420-9cb5ef0fce78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxmYWNpYWwlMjBza2luY2FyZXxlbnwwfHx8fDE3NzU3MzYzNzZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Skincare treatment"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsl(var(--background)), hsl(var(--background) / 0.7) 50%, hsl(var(--background)))' }} />
      </div>

      <FloatingParticles count={15} />

      <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-5xl mb-6 block">💎</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight mb-6">
            Begin Your <span className="text-shimmer-gold italic">Radiance</span> Journey
          </h2>
          <p className="font-body text-base text-muted-foreground max-w-lg mx-auto mb-10 leading-relaxed">
            Join the Leor Radiance Rewards program today and receive 500 Glow Points as a welcome bonus. 
            Every treatment takes you closer to extraordinary rewards.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={handleJoin}
              className="bg-gold hover:bg-gold-light text-accent-foreground font-body text-sm tracking-wide px-8 h-12 rounded-full transition-all duration-300 hover:shadow-gold-lg group"
            >
              <Sparkles className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:rotate-12" />
              Enroll Now — Get 500 GP Free
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('https://www.leorclinics.com/pages/contact', '_blank')}
              className="border-gold/30 text-gold hover:bg-gold/10 hover:text-gold hover:border-gold/50 font-body text-sm tracking-wide px-8 h-12 rounded-full bg-background/30 backdrop-blur-sm transition-all duration-300 group"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8">
            {[
              'GP never expire for top tier',
              'Combine with tier discounts',
              'Redeem across all treatments',
            ].map((text) => (
              <div key={text} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                <span className="font-body text-xs text-muted-foreground">{text}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
